import { Close as CloseIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { type TextFieldProps } from '@mui/material/TextField';
import type { ChangeEvent } from 'react';
import { useMemo } from 'react';

import {
  SelectMenuItem,
  selectMenuStyles,
  SelectNoOptions,
  SelectPlaceholder,
  SelectTextField,
  StyledCircularProgress,
} from './Select.styled.js';
import type { FormSelectOption } from './Select.types.js';

export type SelectProps = {
  options?: FormSelectOption[];
  loading?: boolean;
  errorExists?: boolean;
  errorText?: TextFieldProps['helperText'];
  showClear?: boolean;
  onClear?: () => void;
} & TextFieldProps;

export const Select = ({
  id,
  name,
  value,
  onChange,
  onClear,
  disabled,
  required,
  placeholder,
  slotProps,
  errorExists,
  errorText,
  loading,
  options,
  children,
  showClear = true,
  ...props
}: SelectProps) => {
  const endAdornment = useMemo(() => {
    if (loading) {
      return <StyledCircularProgress size={18} />;
    }

    if (value && showClear) {
      return (
        <Box mr={2}>
          <IconButton
            size="small"
            aria-label="Clear selection"
            sx={{
              'color': theme => theme.palette.grey[800],
              '&:hover': {
                color: theme => theme.palette.text.primary,
              },
            }}
            onClick={() => {
              if (onClear) {
                onClear();
                return;
              }
              onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      );
    }

    return null;
  }, [value, onChange, onClear, loading, showClear]);
  return (
    <SelectTextField
      select
      fullWidth
      margin="none"
      variant="outlined"
      {...props}
      id={id}
      name={name}
      error={errorExists ?? props.error}
      helperText={errorText ?? props.helperText}
      value={value}
      onChange={onChange}
      disabled={disabled}
      required={required}
      slotProps={{
        ...slotProps,
        htmlInput: {
          ...slotProps?.htmlInput,
          'placeholder': placeholder ?? '',
          'data-testid': 'form-select',
        },
        input: {
          ...slotProps?.input,
          endAdornment,
        },
        select: {
          MenuProps: {
            sx: selectMenuStyles,
          },
          renderValue: (value: any) => {
            const label = options?.find(
              option => option.value === value,
            )?.label;
            return value?.length === 0
              ? (
                  <SelectPlaceholder data-sentry-unmask>{placeholder}</SelectPlaceholder>
                )
              : (
                  label
                );
          },
          displayEmpty: true,
          ...slotProps?.select,
        },
      }}
    >
      {children ?? (options?.length === 0
        ? (
            <SelectNoOptions>No options</SelectNoOptions>
          )
        : (
            options?.map(({ label, value }) => {
              return (
                <SelectMenuItem key={label} value={value}>
                  {label}
                </SelectMenuItem>
              );
            })
          ))}
    </SelectTextField>
  );
};
